import React, { useState } from "react";
import { toast } from "react-toastify";
import Dialog from "./Dialog";
import { getApiErrorMessage, getApiErrorStatus } from "../../utils/apiError";

/**
 * Delete with the server's safety check: a 409 ("X is still used by 3 employees…
 * Deactivate it instead.") keeps the dialog open with that message and offers
 * "Deactivate instead" (when onDeactivate is given and the item is active).
 * onDelete / onDeactivate return promises (mutateAsync).
 */
const SafeDeleteDialog = ({
  title = "Delete?",
  name,
  isActive = true,
  onDelete,
  onDeactivate,
  onClose,
}) => {
  const [busy, setBusy] = useState(false);
  const [blocked, setBlocked] = useState(null); // 409 message

  const run = async (fn, success) => {
    setBusy(true);
    try {
      await fn();
      toast.success(success);
      onClose();
    } catch (err) {
      const msg = getApiErrorMessage(err, "Could not complete this.");
      if (getApiErrorStatus(err) === 409)
        setBlocked(Array.isArray(msg) ? msg.join(" ") : msg);
      else toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      title={title}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <button
            type="button"
            className="btn btn-outline-secondary profile-btn"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          {blocked ? (
            onDeactivate &&
            isActive && (
              <button
                type="button"
                className="btn btn-warning profile-btn"
                onClick={() => run(onDeactivate, `${name} deactivated.`)}
                disabled={busy}
              >
                {busy && (
                  <span
                    className="spinner-border spinner-border-sm"
                    aria-hidden="true"
                  ></span>
                )}
                Deactivate instead
              </button>
            )
          ) : (
            <button
              type="button"
              className="btn btn-danger profile-btn"
              onClick={() => run(onDelete, `${name} deleted.`)}
              disabled={busy}
            >
              {busy && (
                <span
                  className="spinner-border spinner-border-sm"
                  aria-hidden="true"
                ></span>
              )}
              Delete
            </button>
          )}
        </>
      }
    >
      {blocked ? (
        <div className="alert alert-warning fs-085 mb-0">
          <i className="bi bi-exclamation-triangle me-1"></i>
          {blocked}
          {!isActive && <div className="mt-1">It is already inactive.</div>}
        </div>
      ) : (
        <p className="fs-085 mb-0">
          Delete <strong>{name}</strong>? This can't be undone. If it is still
          in use, you'll be offered to deactivate it instead.
        </p>
      )}
    </Dialog>
  );
};

export default SafeDeleteDialog;
